import { useState, useCallback, useMemo } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Search } from 'lucide-react';
import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
import { MensajeInformativo } from '../MensajeInformativo';
import type { InputProps } from '../Input/Input';

export interface ValidationResult {
  isValid: boolean;
  message: string;
  data?: Record<string, unknown>;
}

export interface ValidationService {
  (value: string): Promise<ValidationResult>;
}

export interface InputValidadorProps extends Omit<InputProps, 'onChange' | 'rightIcon' | 'variant' | 'validationMessage' | 'showValidationMessage'> {
  value: string;
  onChange: (value: string) => void;
  validationService: ValidationService;
  onValidated: (result: ValidationResult) => void;
  onToast?: (type: 'success' | 'error' | 'warning' | 'info' | 'loading', message: string) => void;
  validateButtonText?: string;
  validatedButtonText?: string;
  loadingText?: string;
  retryText?: string;
  successMessage?: string;
  successDescription?: string;
  minLengthToValidate?: number;
  showValidateButton?: boolean;
  readOnlyAfterValidation?: boolean;
  /** Oculta la línea "SUNAT: razón social" cuando el formulario ya muestra ese dato en otro campo. */
  hideRazonSocialEcho?: boolean;
  /** Mensaje que se muestra pegado debajo del mensaje verde de validación. Solo aparece con el valor validado. */
  notaDebajo?: React.ReactNode;
  /** Oculta el mensaje verde de validación exitosa (p. ej. cuando el formulario muestra un aviso que lo contradice). */
  ocultarMensajeExito?: boolean;
  /** Se llama justo cuando empieza la consulta de validación. */
  onValidationStart?: () => void;
}

export const InputValidador: React.FC<InputValidadorProps> = ({
  value,
  onChange,
  validationService,
  onValidated,
  onToast,
  validateButtonText = "Validar",
  validatedButtonText = "Validado",
  loadingText = "Validando",
  successMessage = "Validación exitosa",
  successDescription,
  minLengthToValidate = 1,
  showValidateButton = true,
  readOnlyAfterValidation = false,
  hideRazonSocialEcho = false,
  notaDebajo,
  ocultarMensajeExito = false,
  onValidationStart,
  disabled = false,
  containerClassName = '',
  ...inputProps
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [lastValidatedValue, setLastValidatedValue] = useState('');
  const [touched, setTouched] = useState(false);

  const isDisabled = disabled || isValidating;
  const isValidated = validation?.isValid && value === lastValidatedValue;
  const canValidate = value.length >= minLengthToValidate && !isDisabled && !isValidated;
  const hasError = validation?.isValid === false;
  const isEmpty = touched && value === '' && !validation;

  const handleValueChange = useCallback((newValue: string) => {
    setTouched(true);
    onChange(newValue);

    if (validation && newValue !== lastValidatedValue) {
      setValidation(null);
      onValidated({ isValid: false, message: 'Valor modificado, necesita validación' });
    }

    if (newValue === '') {
      setValidation(null);
      setLastValidatedValue('');
      onValidated({ isValid: false, message: 'Campo requerido' });
    }
  }, [onChange, validation, lastValidatedValue, onValidated]);

  const handleValidate = useCallback(async () => {
    if (!canValidate || !validationService) return;

    setIsValidating(true);
    setValidation(null);
    onValidationStart?.();

    try {
      onToast?.('loading', loadingText);

      const result = await validationService(value);

      onToast?.(result.isValid ? 'success' : 'error', result.isValid ? `✓ ${successMessage}` : result.message);

      setValidation(result);
      setLastValidatedValue(value);
      onValidated(result);
    } catch {
      const errorResult: ValidationResult = {
        isValid: false,
        message: 'Error de conexión. Intenta nuevamente.',
      };
      setValidation(errorResult);
      onValidated(errorResult);
      onToast?.('error', 'Error de conexión');
    } finally {
      setIsValidating(false);
    }
  }, [canValidate, validationService, value, onToast, loadingText, successMessage, onValidated]);

  const inputVariant = useMemo(() =>
    isValidated ? (readOnlyAfterValidation ? 'validated-readonly' : 'validated-editable') : 'default',
    [isValidated, readOnlyAfterValidation]
  );

  const isInputReadOnly = isValidated && readOnlyAfterValidation;

  const validationMessage = useMemo(() =>
    isValidated ? { title: successMessage, description: "Datos completados automáticamente" } : undefined,
    [isValidated, successMessage]
  );

  const showInputValidationMessage = isValidated && !validation?.data;
  const errorMessage = hasError ? validation.message : inputProps.error;

  return (
    <div className={`${containerClassName}`}>
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <Input
            {...inputProps}
            value={value}
            onValueChange={handleValueChange}
            disabled={isDisabled}
            readOnly={isInputReadOnly}
            variant={inputVariant}
            validationMessage={validationMessage}
            showValidationMessage={showInputValidationMessage}
            error={errorMessage}
            hideErrorMessage
            containerClassName=""
          />
        </div>

        {showValidateButton && (
          <div className="shrink-0 pb-px">
            <Button
              type="button"
              onClick={handleValidate}
              disabled={!canValidate}
              loading={isValidating}
              loadingText={loadingText}
              variant="primary"
              size="md"
              icon={isValidated ? <CheckCircle2 /> : <Search />}
              className={`h-10 whitespace-nowrap ${isValidated ? 'bg-green-600 hover:bg-green-700 active:bg-green-800 focus:ring-green-500' : ''}`}
            >
              {isValidated ? validatedButtonText : validateButtonText}
            </Button>
          </div>
        )}
      </div>

      {/* Mensaje de error: se muestra aquí (fuera de la fila) para que no afecte la alineación del botón */}
      {errorMessage && (
        <div className="flex items-center gap-1.5 text-caption text-error mt-2">
          <AlertCircle size={14} />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Mensajes de validación */}
      {isValidated && validation && !ocultarMensajeExito && (
        <div className="mt-1.5 animate-in slide-in-from-top-2 duration-300">
          <MensajeInformativo variant="success" unido={notaDebajo ? 'abajo' : undefined}>
            <span className="font-semibold">{successMessage}</span>
            {successDescription && <span> {successDescription}</span>}
            {!validation.data && (
              <span className="block mt-0.5">{validation.message}</span>
            )}
            {!hideRazonSocialEcho && !!validation.data?.razonSocial && (
              <span className="block mt-0.5">
                SUNAT: {String(validation.data.razonSocial)}
              </span>
            )}
          </MensajeInformativo>
          {notaDebajo}
        </div>
      )}

      {isEmpty && (
        <p className="text-xs text-error flex items-center gap-1.5 mt-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          Este campo es obligatorio
        </p>
      )}
    </div>
  );
};

InputValidador.displayName = 'InputValidador';

